import { ref, shallowRef, computed, watch } from 'vue'

// Module-level singleton behind the right-hand workspace sidebar. One instance
// (WorkspaceSidebar.vue) is rendered by AppRoot and teleported into whichever
// shell claimed it — AdminLayout's column, or the wide Dashboard's right-hand
// column — so moving between them keeps every tab (a draft, the signups list's
// filters) alive instead of remounting it. Anything can open a panel in it by
// type — global search, the header's Actions menu, the n/m/u shortcuts, the
// calendar's click-to-create — without that page mounting its own copy.
//
// The sidebar holds tabs, one shown at a time, so an unfinished form is never
// thrown away. What gets a tab:
//   - contact: one reusable tab — opening another contact swaps it in place,
//     so clicking through the inbox doesn't pile up tabs.
//   - signups: one tab; opening it again just switches to it. On the Dashboard
//     it's pinned (can't be closed — × / Esc there go back to Attendance).
//   - forms: a tab each, so a draft is never replaced. Re-opening one with the
//     same props switches to it (keeps the draft); different props (another
//     calendar slot) is a new tab.
// Plus ATTENDANCE and INVOICES (teachers): not tab entries but Dashboard-only
// views that activeKey can point at, created only while shown (Attendance's
// auto-scroll measures layout).
// Closing (× / Cancel / Esc / Save) returns to the tab it was opened from if
// that's still open, else its neighbour.
//
// `available` is true only while a shell hosts the sidebar. Callers that also
// run elsewhere (the narrow Dashboard, AppRoot's shortcuts) check it and fall
// back to their popup modal there.
const SINGLE_TAB_TYPES = new Set(['contact', 'signups'])
export const ATTENDANCE = 'attendance'
export const INVOICES = 'invoices'

const tabs = ref([])        // [{ type, props, onSaved, key, opener, title }]
const activeKey = ref(null)
const host = ref(null)      // 'admin' | 'dashboard' | null
const hostProps = shallowRef({})  // the Dashboard's getters: { teacherName, showInvoices }
const available = computed(() => !!host.value)
let seq = 0

const onDashboard = () => host.value === 'dashboard'
const isPinned = (e) => onDashboard() && e.type === 'signups'
const current = computed(() => tabs.value.find(t => t.key === activeKey.value) || null)
const contactTab = computed(() => tabs.value.find(t => t.type === 'contact') || null)
// The contact on screen (null while another tab is in front) …
const openContactId = computed(() => current.value?.type === 'contact' ? current.value.props.contactId : null)
// … vs. the contact tab's contact, shown or not.
const contactTabId = computed(() => contactTab.value?.props.contactId ?? null)

// Bumped when a contact is edited from the sidebar, so a page listing contacts
// (the Directory) can refetch.
const contactsChanged = ref(0)

// A shell takes the sidebar while it's on screen and gives it back when it
// goes; release only clears our own claim, so the next shell's claim (which
// can land first during a route change) isn't undone.
function claimHost(name, props = {}) {
  host.value = name
  hostProps.value = props
  if (name === 'dashboard' && (activeKey.value == null || activeKey.value === ATTENDANCE)) {
    const saved = localStorage.getItem('sidebarTab')
    const signups = tabs.value.find(t => t.type === 'signups')
    activeKey.value = (saved === 'signups' && signups) ? signups.key
      : (saved === INVOICES && props.showInvoices?.()) ? INVOICES : ATTENDANCE
  }
}
function releaseHost(name) {
  if (host.value === name) host.value = null
}
// The Dashboard remembers Attendance / Invoices / Signups across reloads.
watch(activeKey, (key) => {
  if (!onDashboard()) return
  const type = (key === ATTENDANCE || key === INVOICES) ? key : tabs.value.find(t => t.key === key)?.type
  if (type === ATTENDANCE || type === INVOICES || type === 'signups') localStorage.setItem('sidebarTab', type)
})

const sameProps = (a, b) => JSON.stringify(a) === JSON.stringify(b)

function findTab(type, props) {
  return tabs.value.find(t => t.type === type && (SINGLE_TAB_TYPES.has(type) || sameProps(t.props, props)))
}

// Adds a tab, or points a single-tab type's existing tab at the new props in
// place (same key, so tabs opened from it still return to it). Returns the tab;
// doesn't change which tab is shown.
function upsert(type, props, onSaved) {
  const existing = findTab(type, props)
  if (existing) {
    if (!sameProps(existing.props, props)) Object.assign(existing, { props, onSaved, title: '' })
    return existing
  }
  const entry = { type, props, onSaved, key: ++seq, opener: activeKey.value, title: '' }
  tabs.value = [...tabs.value, entry]
  return entry
}

// props: the panel component's props. onSaved: forms call it after a successful save.
function open(type, props = {}, { onSaved = null } = {}) {
  activeKey.value = upsert(type, props, onSaved).key
}

// For views whose selection has a contact (the inboxes): keep the contact tab
// on it, but only bring it to the front if a contact was already showing —
// never cover a form or the signups list someone is working in.
function followContact(props) {
  const entry = upsert('contact', props, null)
  if (!current.value || current.value.type === 'contact') activeKey.value = entry.key
}

function close(key = activeKey.value) {
  const i = tabs.value.findIndex(t => t.key === key)
  if (i === -1) return
  const gone = tabs.value[i]
  if (isPinned(gone)) { activeKey.value = ATTENDANCE; return }
  tabs.value = tabs.value.filter(t => t !== gone)
  if (activeKey.value !== key) return
  if (onDashboard() && gone.opener === ATTENDANCE) { activeKey.value = ATTENDANCE; return }
  const opener = tabs.value.find(t => t.key === gone.opener)
  activeKey.value = (opener || tabs.value[Math.min(i, tabs.value.length - 1)])?.key ?? (onDashboard() ? ATTENDANCE : null)
}
function closeType(type) { tabs.value.filter(t => t.type === type).forEach(t => close(t.key)) }
function closeAll() { tabs.value = []; activeKey.value = null }

// Shortcut behaviour: show it, or close it if it's already in front.
function toggle(type, props = {}) {
  if (current.value?.type === type && (SINGLE_TAB_TYPES.has(type) || sameProps(current.value.props, props))) close()
  else open(type, props)
}

export function useAdminSidebar() {
  return {
    tabs, activeKey, current, available, host, hostProps, openContactId, contactTabId, contactsChanged,
    open, followContact, toggle, close, closeType, closeAll, claimHost, releaseHost, isPinned,
    // Adds a tab without showing it (the Dashboard's pinned Signups).
    ensure: (type, props = {}) => upsert(type, props, null),
    back: () => close(),
    activate: (key) => { activeKey.value = key },
    notifyContactsChanged: () => { contactsChanged.value++ },
  }
}
